<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Auth from 'dashboard/api/auth';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';

const forceDesktopView = defineModel('forceDesktopView', {
  type: Boolean,
  default: false,
});

const { t } = useI18n();
const store = useStore();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentUserAcceptsChat = useMapGetter('getCurrentUserAcceptsChat');
const currentAccountId = useMapGetter('getCurrentAccountId');

const darkModeEnabled = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) === 'dark'
);

const profileStatus = computed(() =>
  currentUserAvailability.value === 'online' ? 'online' : 'offline'
);

const profileName = computed(
  () =>
    currentUser.value?.display_name ||
    currentUser.value?.available_name ||
    currentUser.value?.name ||
    'Profile'
);

const profileEmail = computed(() => currentUser.value?.email || 'No email');

const acceptsChats = computed({
  get: () => currentUserAcceptsChat.value !== false,
  set: acceptsChat => {
    store.dispatch('updateAcceptsChat', {
      accountId: currentAccountId.value,
      acceptsChat,
    });
  },
});

const setDarkMode = enabled => {
  darkModeEnabled.value = enabled;
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.COLOR_SCHEME,
    enabled ? 'dark' : 'light'
  );
  const isOSOnDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches;
  setColorTheme(isOSOnDarkMode);
};

const openDesktopProfileView = () => {
  forceDesktopView.value = true;
};

const logout = async () => {
  try {
    await Auth.logout();
  } catch (error) {
    // Ignore here; logout helper redirects on success.
  }
};
</script>

<template>
  <div class="mobile-profile-page">
    <header class="mobile-profile-page__header">
      <div class="mobile-profile-page__header-spacer" />
      <h1 class="mobile-profile-page__title">
        {{ $t('PROFILE_SETTINGS.TITLE') }}
      </h1>
      <div class="mobile-profile-page__header-spacer" />
    </header>

    <div class="mobile-profile-page__body">
      <button
        type="button"
        class="mobile-profile-page__summary"
        @click="openDesktopProfileView"
      >
        <Avatar
          :src="currentUser.avatar_url"
          :name="profileName"
          :status="profileStatus"
          :size="64"
          rounded-full
        />
        <div class="mobile-profile-page__summary-content">
          <span class="mobile-profile-page__summary-name">
            {{ profileName }}
          </span>
          <span class="mobile-profile-page__summary-email">
            {{ profileEmail }}
          </span>
        </div>
        <span
          class="i-ph-caret-right-bold mobile-profile-page__summary-arrow"
        />
      </button>

      <ul class="mobile-profile-page__menu">
        <li class="mobile-profile-page__menu-item">
          <div class="mobile-profile-page__menu-copy">
            <span class="mobile-profile-page__menu-label">Accept Chats</span>
          </div>
          <ToggleSwitch v-model="acceptsChats" />
        </li>

        <li class="mobile-profile-page__menu-item">
          <div class="mobile-profile-page__menu-copy">
            <span class="mobile-profile-page__menu-label">Dark mode</span>
            <small class="mobile-profile-page__menu-note">Chats only</small>
          </div>
          <ToggleSwitch
            :model-value="darkModeEnabled"
            @update:model-value="setDarkMode"
          />
        </li>

        <li class="mobile-profile-page__menu-item">
          <div class="mobile-profile-page__menu-copy">
            <span class="mobile-profile-page__menu-label"
              >Force desktop view</span
            >
            <small class="mobile-profile-page__menu-note"
              >Some content may not display correctly</small
            >
          </div>
          <ToggleSwitch v-model="forceDesktopView" />
        </li>
      </ul>

      <button type="button" class="mobile-profile-page__logout" @click="logout">
        {{ t('SIDEBAR_ITEMS.LOGOUT') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.mobile-profile-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #ffffff;
  color: #131317;
}

.mobile-profile-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  min-height: 56px;
  border-bottom: 1px solid #e5e5e6;
  background: #ffffff;
  padding: 0 12px;
}

.mobile-profile-page__header-spacer {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.mobile-profile-page__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #131317;
}

.mobile-profile-page__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #ffffff;
}

.mobile-profile-page__summary {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0;
  padding: 20px 22px;
  border: 0;
  background: #ffffff;
  text-align: left;
}

.mobile-profile-page__summary-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  padding-left: 20px;
}

.mobile-profile-page__summary-name {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: #131317;
}

.mobile-profile-page__summary-email {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.35;
  color: #68707d;
}

.mobile-profile-page__summary-arrow {
  width: 20px;
  height: 20px;
  margin-left: 20px;
  flex-shrink: 0;
  color: #0059e1;
}

.mobile-profile-page__menu {
  margin: 0;
  padding: 0 10px;
  list-style: none;
}

.mobile-profile-page__menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 19px 12px;
  border-bottom: 1px solid #e5e5e6;
}

.mobile-profile-page__menu-item:first-child {
  border-top: 1px solid #e5e5e6;
}

.mobile-profile-page__menu-copy {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
}

.mobile-profile-page__menu-label {
  font-size: 14px;
  line-height: 1.3;
  color: #131317;
}

.mobile-profile-page__menu-note {
  margin-top: 3px;
  font-size: 13px;
  line-height: 1.3;
  color: #68707d;
}

.mobile-profile-page__logout {
  display: block;
  width: calc(100% - 20px);
  margin: 0 10px;
  padding: 19px 12px;
  border: 0;
  border-bottom: 1px solid #e5e5e6;
  background: #ffffff;
  text-align: left;
  font-size: 14px;
  line-height: 19px;
  letter-spacing: 0.3px;
  color: #131317;
}
</style>
